import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

interface Country {
  code: string;
  name: string;
  dialCode: string;
  flag: string; // Flag emoji
}

// Helper function to get flag emoji from country code
const getFlagEmoji = (countryCode: string): string => {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

const COUNTRIES: Country[] = [
  { code: 'nz', name: 'New Zealand', dialCode: '+64', flag: getFlagEmoji('NZ') },
  { code: 'au', name: 'Australia', dialCode: '+61', flag: getFlagEmoji('AU') },
  { code: 'us', name: 'United States', dialCode: '+1', flag: getFlagEmoji('US') },
  { code: 'gb', name: 'United Kingdom', dialCode: '+44', flag: getFlagEmoji('GB') },
  { code: 'ca', name: 'Canada', dialCode: '+1', flag: getFlagEmoji('CA') },
  { code: 'za', name: 'South Africa', dialCode: '+27', flag: getFlagEmoji('ZA') },
  { code: 'ie', name: 'Ireland', dialCode: '+353', flag: getFlagEmoji('IE') },
  { code: 'sg', name: 'Singapore', dialCode: '+65', flag: getFlagEmoji('SG') },
  { code: 'my', name: 'Malaysia', dialCode: '+60', flag: getFlagEmoji('MY') },
  { code: 'ph', name: 'Philippines', dialCode: '+63', flag: getFlagEmoji('PH') },
  { code: 'id', name: 'Indonesia', dialCode: '+62', flag: getFlagEmoji('ID') },
  { code: 'th', name: 'Thailand', dialCode: '+66', flag: getFlagEmoji('TH') },
  { code: 'vn', name: 'Vietnam', dialCode: '+84', flag: getFlagEmoji('VN') },
  { code: 'jp', name: 'Japan', dialCode: '+81', flag: getFlagEmoji('JP') },
  { code: 'kr', name: 'South Korea', dialCode: '+82', flag: getFlagEmoji('KR') },
  { code: 'cn', name: 'China', dialCode: '+86', flag: getFlagEmoji('CN') },
  { code: 'in', name: 'India', dialCode: '+91', flag: getFlagEmoji('IN') },
  { code: 'fr', name: 'France', dialCode: '+33', flag: getFlagEmoji('FR') },
  { code: 'de', name: 'Germany', dialCode: '+49', flag: getFlagEmoji('DE') },
  { code: 'it', name: 'Italy', dialCode: '+39', flag: getFlagEmoji('IT') },
  { code: 'es', name: 'Spain', dialCode: '+34', flag: getFlagEmoji('ES') },
  { code: 'nl', name: 'Netherlands', dialCode: '+31', flag: getFlagEmoji('NL') },
  { code: 'be', name: 'Belgium', dialCode: '+32', flag: getFlagEmoji('BE') },
  { code: 'ch', name: 'Switzerland', dialCode: '+41', flag: getFlagEmoji('CH') },
  { code: 'at', name: 'Austria', dialCode: '+43', flag: getFlagEmoji('AT') },
  { code: 'se', name: 'Sweden', dialCode: '+46', flag: getFlagEmoji('SE') },
  { code: 'no', name: 'Norway', dialCode: '+47', flag: getFlagEmoji('NO') },
  { code: 'dk', name: 'Denmark', dialCode: '+45', flag: getFlagEmoji('DK') },
  { code: 'fi', name: 'Finland', dialCode: '+358', flag: getFlagEmoji('FI') },
  { code: 'pl', name: 'Poland', dialCode: '+48', flag: getFlagEmoji('PL') },
  { code: 'pt', name: 'Portugal', dialCode: '+351', flag: getFlagEmoji('PT') },
  { code: 'br', name: 'Brazil', dialCode: '+55', flag: getFlagEmoji('BR') },
  { code: 'mx', name: 'Mexico', dialCode: '+52', flag: getFlagEmoji('MX') },
  { code: 'ar', name: 'Argentina', dialCode: '+54', flag: getFlagEmoji('AR') },
  { code: 'cl', name: 'Chile', dialCode: '+56', flag: getFlagEmoji('CL') },
  { code: 'co', name: 'Colombia', dialCode: '+57', flag: getFlagEmoji('CO') },
  { code: 'pe', name: 'Peru', dialCode: '+51', flag: getFlagEmoji('PE') },
  { code: 'ae', name: 'UAE', dialCode: '+971', flag: getFlagEmoji('AE') },
  { code: 'sa', name: 'Saudi Arabia', dialCode: '+966', flag: getFlagEmoji('SA') },
  { code: 'il', name: 'Israel', dialCode: '+972', flag: getFlagEmoji('IL') },
  { code: 'tr', name: 'Turkey', dialCode: '+90', flag: getFlagEmoji('TR') },
  { code: 'eg', name: 'Egypt', dialCode: '+20', flag: getFlagEmoji('EG') },
  { code: 'ng', name: 'Nigeria', dialCode: '+234', flag: getFlagEmoji('NG') },
  { code: 'ke', name: 'Kenya', dialCode: '+254', flag: getFlagEmoji('KE') },
];

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  defaultCountry?: string; // ISO country code (e.g., 'nz', 'au')
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  defaultCountry,
  placeholder,
  className = '',
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<Country>(COUNTRIES[0]);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);

  // Helper function to convert country name to ISO code
  const getCountryCode = (countryName: string | undefined): string | undefined => {
    if (!countryName) return undefined;
    
    const countryMap: { [key: string]: string } = {
      'new zealand': 'nz',
      'australia': 'au',
      'united states': 'us',
      'united states of america': 'us',
      'usa': 'us',
      'united kingdom': 'gb',
      'uk': 'gb',
      'canada': 'ca',
      'south africa': 'za',
      'ireland': 'ie',
      'singapore': 'sg',
      'malaysia': 'my',
      'philippines': 'ph',
      'indonesia': 'id',
      'thailand': 'th',
      'vietnam': 'vn',
      'japan': 'jp',
      'south korea': 'kr',
      'china': 'cn',
      'india': 'in',
      'france': 'fr',
      'germany': 'de',
      'italy': 'it',
      'spain': 'es',
      'netherlands': 'nl',
      'belgium': 'be',
      'switzerland': 'ch',
      'austria': 'at',
      'sweden': 'se',
      'norway': 'no',
      'denmark': 'dk',
      'finland': 'fi',
      'poland': 'pl',
      'portugal': 'pt',
      'brazil': 'br',
      'mexico': 'mx',
      'argentina': 'ar',
      'chile': 'cl',
      'colombia': 'co',
      'peru': 'pe',
      'uae': 'ae',
      'united arab emirates': 'ae',
      'saudi arabia': 'sa',
      'israel': 'il',
      'turkey': 'tr',
      'egypt': 'eg',
      'nigeria': 'ng',
      'kenya': 'ke'
    };
    
    const normalized = countryName.toLowerCase().trim();
    return countryMap[normalized];
  };

  // Initialize country from defaultCountry
  useEffect(() => {
    if (defaultCountry && !isInitialized) {
      const country = COUNTRIES.find(c => c.code === defaultCountry.toLowerCase());
      if (country) {
        setSelectedCountry(country);
      }
      setIsInitialized(true);
    }
  }, [defaultCountry]); // eslint-disable-line react-hooks/exhaustive-deps

  // Parse existing value to extract country and phone number (only on initial mount)
  useEffect(() => {
    if (value && !phoneNumber && !isInitialized) {
      // Check if value starts with a dial code
      const matchingCountry = COUNTRIES.find(country => value.startsWith(country.dialCode));
      if (matchingCountry) {
        setSelectedCountry(matchingCountry);
        let number = value.substring(matchingCountry.dialCode.length).trim().replace(/\D/g, '');
        // Remove leading zeros from parsed number
        if (number.length > 1 && number.startsWith('0')) {
          number = number.substring(1);
        }
        if (number.length > 0) {
          setPhoneNumber(number);
        }
      } else {
        // Assume it's just the phone number
        let number = value.replace(/\D/g, '');
        // Remove leading zeros if present
        if (number.length > 1 && number.startsWith('0')) {
          number = number.substring(1);
        }
        if (number.length > 0) {
          setPhoneNumber(number);
        }
      }
      setIsInitialized(true);
    } else if (!value && isInitialized && phoneNumber) {
      // Value was cleared externally
      setPhoneNumber('');
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update parent when country or phone number changes (but only after initialization)
  useEffect(() => {
    if (!isInitialized) return; // Don't update during initialization
    
    // Only update if we have an actual phone number (not just country code)
    if (phoneNumber && phoneNumber.length > 0) {
      const fullNumber = `${selectedCountry.dialCode}${phoneNumber}`;
      // Only update if the value actually changed
      if (fullNumber !== value) {
        onChange(fullNumber);
      }
    } else if (value && value.length > 0 && phoneNumber.length === 0) {
      // Phone number was cleared but value still exists, clear it
      onChange('');
    }
  }, [selectedCountry, phoneNumber, isInitialized]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country);
    setIsOpen(false);
    // Keep the phone number, but remove leading zeros if present
    let number = phoneNumber;
    if (number && number.length > 1 && number.startsWith('0')) {
      number = number.substring(1);
      setPhoneNumber(number);
    }
    // Only update if we have actual digits
    if (number && number.length > 0) {
      const fullNumber = `${country.dialCode}${number}`;
      onChange(fullNumber);
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value.replace(/\D/g, ''); // Only digits
    
    // Remove leading zeros when country code is present (international format doesn't use leading 0)
    // Allow 0 if it's the only digit, but remove it if there are more digits
    if (input.length > 1 && input.startsWith('0')) {
      input = input.substring(1);
    }
    
    setPhoneNumber(input);
    setIsInitialized(true); // Mark as initialized when user starts typing
    
    // Only call onChange if we have actual digits (more than just country code)
    // Country codes are 2-4 digits, so we need at least 1 more digit
    if (input && input.length > 0) {
      const fullNumber = `${selectedCountry.dialCode}${input}`;
      onChange(fullNumber);
    } else {
      // Clear the value if no digits
      onChange('');
    }
  };

  return (
    <div className={`flex ${className}`}>
      {/* Country Selector */}
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className="px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-l-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-accent-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 min-w-[100px] transition-colors"
        >
          <span className="text-lg leading-none" role="img" aria-label={selectedCountry.name}>
            {selectedCountry.flag}
          </span>
          <span className="text-sm font-medium">{selectedCountry.dialCode}</span>
          <ChevronDown className="w-4 h-4 flex-shrink-0" />
        </button>

        {isOpen && !disabled && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setIsOpen(false)}
            />
            <div className="absolute z-20 mt-1 w-72 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl max-h-80 overflow-y-auto">
              <div className="p-1">
                {COUNTRIES.map((country) => (
                  <button
                    key={country.code}
                    type="button"
                    onClick={() => handleCountrySelect(country)}
                    className={`w-full text-left px-3 py-2.5 rounded-md transition-colors ${
                      selectedCountry.code === country.code
                        ? 'bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50 text-accent-600 dark:text-accent-400'
                        : 'text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <span className="text-xl leading-none flex-shrink-0" role="img" aria-label={country.name}>
                          {country.flag}
                        </span>
                        <span className="text-sm font-medium truncate">{country.name}</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-600 dark:text-gray-400 flex-shrink-0">
                        {country.dialCode}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Phone Number Input */}
      <input
        type="tel"
        value={phoneNumber}
        onChange={handlePhoneChange}
        onKeyDown={(e) => {
          // Prevent form submission on Enter key
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        }}
        placeholder={placeholder || 'Phone number'}
        disabled={disabled}
        className="flex-1 px-4 py-2.5 border border-l-0 border-gray-300 dark:border-gray-600 rounded-r-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-accent-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm transition-colors"
      />
    </div>
  );
};

